import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

// Typy pre plány
interface SubscriptionPlan {
  id: number;
  name: string;
  description: string;
  locale: string;
  currency: string;
  price: string;
  billing_interval: string;
  paddle_plan_id: string;
  max_clients: number;
  max_invoices: number;
  max_tasks: number;
  max_team_members: number;
  max_quotes: number;
  max_storage: number;
  is_active: boolean;
  features?: string[];
  intro_price?: string;
  intro_duration?: string;
}

interface ChangePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPlan?: string;
  currentUserEmail?: string;
  locale?: string;
}

// Deklarácia pre Paddle
declare global {
  interface Window {
    Paddle?: {
      Payments: {
        checkout: (options: {
          items: Array<{ plan: string }>;
          customer?: { email: string };
          onPaymentSuccess?: () => void;
          onPaymentError?: (error: any) => void;
        }) => void;
      };
    };
  }
}

export function ChangePlanModal({ 
  isOpen, 
  onClose, 
  currentPlan, 
  currentUserEmail, 
  locale = 'sk' 
}: ChangePlanModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState<string | null>(null);

  // Načítanie dostupných plánov
  const { data: plans, isLoading: plansLoading } = useQuery<SubscriptionPlan[]>({
    queryKey: ['/api/plans', locale],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/plans?locale=${locale}`);
      return response.json();
    },
    enabled: isOpen,
  });

  // Načítanie Paddle SDK
  useEffect(() => {
    if (!isOpen) return;

    const loadPaddleScript = () => {
      // Skontrolujeme či už je Paddle načítané
      if (window.Paddle) return Promise.resolve();

      return new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
        script.async = true;
        script.onload = () => {
          // Inicializácia Paddle s verejným kľúčom
          if (window.Paddle) {
            resolve();
          } else {
            reject(new Error('Paddle failed to load'));
          }
        };
        script.onerror = () => reject(new Error('Failed to load Paddle script'));
        document.head.appendChild(script);
      });
    };

    loadPaddleScript().catch(error => {
      console.error('Error loading Paddle:', error);
      toast({
        title: "Informácia",
        description: "Nepodarilo se načítať platobný systém",
        variant: "default",
      });
    });
  }, [isOpen, toast]);

  // Mutácia pre spracovanie zmeny plánu
  const changeUserPlan = useMutation({
    mutationFn: async ({ planId, paddlePlanId }: { planId: number; paddlePlanId: string }) => {
      // Tu by sme mohli pridať API call pre aktualizáciu plánu
      return { planId, paddlePlanId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/user'] });
      toast({
        title: "Úspech",
        description: "Plán bol úspešne zmenený",
      });
      onClose();
    },
    onError: (error: any) => {
      toast({
        title: "Informácia",
        description: error.message || "Nepodarilo sa zmeniť plán",
        variant: "default",
      });
    },
  });

  const handleSelectPlan = async (plan: SubscriptionPlan) => {
    if (!window.Paddle) {
      toast({
        title: "Informácia",
        description: "Platobný systém nie je načítaný",
        variant: "default",
      });
      return;
    }

    if (!currentUserEmail) {
      toast({
        title: "Informácia",
        description: "Email používateľa nie je dostupný",
        variant: "default",
      });
      return;
    }

    setIsProcessing(plan.paddle_plan_id);

    try {
      window.Paddle.Payments.checkout({
        items: [{ plan: plan.paddle_plan_id }],
        customer: { email: currentUserEmail },
        onPaymentSuccess: () => {
          setIsProcessing(null);
          changeUserPlan.mutate({ 
            planId: plan.id, 
            paddlePlanId: plan.paddle_plan_id 
          });
        },
        onPaymentError: (error) => {
          setIsProcessing(null);
          console.error('Paddle payment error:', error);
          toast({
            title: "Chyba platby",
            description: "Nepodarilo sa spracovať platbu",
            variant: "default",
          });
        },
      });
    } catch (error) {
      setIsProcessing(null);
      console.error('Error initiating Paddle checkout:', error);
      toast({
        title: "Informácia",
        description: "Nepodarilo sa spustiť platbu",
        variant: "default",
      });
    }
  };

  const formatPrice = (price: string, currency: string, interval: string) => {
    const formattedPrice = parseFloat(price).toFixed(2);
    const currencySymbol = currency === 'EUR' ? '€' : '$';
    const intervalText = interval === 'monthly' ? 'mesiac' : 'rok';
    return `${formattedPrice} ${currencySymbol}/${intervalText}`;
  };

  const isCurrentPlan = (planName: string) => {
    return currentPlan === planName;
  };

  const getPlanBenefits = (plan: SubscriptionPlan): string[] => {
    const benefits = [];
    
    if (plan.max_clients > 0) {
      benefits.push(plan.max_clients === 999999 ? 'Neobmedzení klienti' : `${plan.max_clients} klientov`);
    }
    if (plan.max_tasks > 0) {
      benefits.push(plan.max_tasks === 999999 ? 'Neobmedzené úlohy' : `${plan.max_tasks} úloh`);
    }
    if (plan.max_invoices > 0) {
      benefits.push(plan.max_invoices === 999999 ? 'Neobmedzené faktúry' : `${plan.max_invoices} faktúr`);
    }
    if (plan.max_team_members > 0) {
      benefits.push(plan.max_team_members === 999999 ? 'Neobmedzení členovia tímu' : `${plan.max_team_members} členov tímu`);
    }
    if (plan.max_quotes > 0) {
      benefits.push(plan.max_quotes === 999999 ? 'Neobmedzené cenové ponuky' : `${plan.max_quotes} cenových ponúk`);
    }
    
    return benefits;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-background/80 backdrop-blur-xl shadow-2xl border-l-4 border-l-primary/30">
        <DialogHeader>
          <DialogTitle>Zmeniť predplatný plán</DialogTitle>
          <DialogDescription>
            Vyberte nový plán, ktorý najlepšie vyhovuje vašim potrebám
          </DialogDescription>
        </DialogHeader>

        {plansLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
            <span className="ml-2">Načítavam plány...</span>
          </div>
        ) : plans && plans.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {plans.filter(plan => plan.is_active).map((plan) => (
              <Card key={plan.id} className={`relative ${isCurrentPlan(plan.name) ? 'border-primary' : ''}`}>
                {isCurrentPlan(plan.name) && (
                  <div className="absolute -top-2 -right-2">
                    <Badge variant="default" className="bg-green-500 hover:bg-green-600">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Aktuálny plán
                    </Badge>
                  </div>
                )}
                
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    {plan.name}
                    <span className="text-lg font-bold">
                      {formatPrice(plan.price, plan.currency, plan.billing_interval)}
                    </span>
                  </CardTitle>
                  {plan.description && (
                    <CardDescription>{plan.description}</CardDescription>
                  )}
                  
                  {plan.intro_price && plan.intro_duration && (
                    <div className="bg-orange-50 dark:bg-orange-900/20 p-2 rounded-md">
                      <p className="text-sm text-orange-700 dark:text-orange-300">
                        {plan.intro_duration} za {parseFloat(plan.intro_price).toFixed(2)} {plan.currency === 'EUR' ? '€' : '$'}
                      </p>
                    </div>
                  )}
                </CardHeader>

                <CardContent>
                  <div className="space-y-2">
                    {getPlanBenefits(plan).map((benefit, idx) => (
                      <div key={idx} className="flex items-center text-sm">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                        {benefit}
                      </div>
                    ))}
                  </div>
                </CardContent>

                <CardFooter>
                  <Button
                    className="w-full"
                    variant={isCurrentPlan(plan.name) ? "outline" : "default"}
                    disabled={isCurrentPlan(plan.name) || isProcessing !== null}
                    onClick={() => handleSelectPlan(plan)}
                  >
                    {isProcessing === plan.paddle_plan_id ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Spracováva sa...
                      </>
                    ) : isCurrentPlan(plan.name) ? (
                      "✓ Aktuálny plán"
                    ) : (
                      "Vybrať plán"
                    )}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">Žiadne plány nie sú dostupné</p>
            <p className="text-sm text-muted-foreground">
              Kontaktujte administrátora pre nastavenie plánov.
            </p>
          </div>
        )}

        <div className="flex justify-end mt-6">
          <Button variant="outline" onClick={onClose}>
            Zavrieť
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}